import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import {
  ArrowRight, School, Building2, Library, TreePine, CheckCircle2,
  Star, Users, BarChart3, BookOpen, Shield, Handshake, Quote
} from 'lucide-react';

const partnerTypes = [
  { icon: School, title: 'K-12 Schools', desc: 'After-school and in-school coding programs tailored to your curriculum standards.', color: 'bg-blue-500' },
  { icon: Library, title: 'Libraries', desc: 'Free community coding workshops hosted at your local library branches.', color: 'bg-purple-500' },
  { icon: TreePine, title: 'Park Districts', desc: 'Seasonal coding camps and enrichment programs for park district families.', color: 'bg-green-500' },
  { icon: Building2, title: 'Community Centers', desc: 'Year-round STEM programs bringing coding education to neighborhood centers.', color: 'bg-orange-500' },
];

const testimonials = [
  {
    quote: "Ready Set STEM transformed our after-school program. Students who never showed interest in technology are now building their own games and websites. The curriculum is perfectly aligned with our learning standards.",
    author: "Dr. Jennifer Martinez",
    role: "Principal, Lincoln Elementary School",
    org: "Aurora School District",
    rating: 5,
  },
  {
    quote: "As a park district, finding quality enrichment programs is challenging. Ready Set STEM delivered exceptional coding camps that parents loved and kids couldn't stop talking about. We've renewed our partnership for the third year.",
    author: "Mark Thompson",
    role: "Recreation Director",
    org: "Fox Valley Park District",
    rating: 5,
  },
  {
    quote: "Our library coding workshops are always fully booked within days. The RSC team provides everything — curriculum, materials, and passionate instructors. It's been a tremendous value-add for our community.",
    author: "Sarah Kim",
    role: "Youth Services Director",
    org: "Aurora Public Library",
    rating: 5,
  },
  {
    quote: "The data and reporting Ready Set STEM provides helps us demonstrate the impact to our school board. We can track student progress, attendance, and skill development — it's exactly what we needed.",
    author: "Robert Chen",
    role: "STEM Coordinator",
    org: "Naperville Community Unit School District",
    rating: 5,
  },
];

const benefits = [
  { icon: BookOpen, title: 'Turnkey Curriculum', desc: 'STEM-accredited curriculum with lesson plans, materials, and assessments included.' },
  { icon: Users, title: 'Trained Instructors', desc: 'We provide background-checked, trained coding instructors for every program.' },
  { icon: BarChart3, title: 'Impact Reporting', desc: 'Monthly reports on student progress, attendance, and skill development metrics.' },
  { icon: Shield, title: 'Fully Insured', desc: 'All programs include comprehensive insurance and safety protocols.' },
  { icon: Star, title: 'Customizable Programs', desc: 'Programs tailored to your schedule, age group, and learning objectives.' },
  { icon: Handshake, title: 'Dedicated Support', desc: 'A dedicated partnership manager to ensure smooth program delivery.' },
];

const partnerLogos = [
  { name: 'Aurora School District 129', initials: 'ASD' },
  { name: 'Indian Prairie District 204', initials: 'IP204' },
  { name: 'Fox Valley Park District', initials: 'FVPD' },
  { name: 'Aurora Public Library', initials: 'APL' },
  { name: 'Naperville CUSD 203', initials: 'N203' },
  { name: 'Oswego Community Unit', initials: 'OCUD' },
  { name: 'Kane County YMCA', initials: 'YMCA' },
  { name: 'Boys & Girls Club', initials: 'BGC' },
];

export default function PartnershipsPage() {
  return (
    <div data-testid="partnerships-page">
      {/* Hero */}
      <section className="relative py-24 md:py-32 gradient-hero overflow-hidden">
        <div className="container-app relative z-10">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/20 text-white text-sm font-semibold mb-6">
              <Handshake className="w-4 h-4" /> School & Community Partnerships
            </div>
            <h1 className="font-heading text-4xl md:text-6xl font-extrabold text-white mb-6">
              Bring Coding Education<br />
              <span className="text-rsc-yellow">To Your Community</span>
            </h1>
            <p className="text-xl text-white/80 mb-8">
              Partner with Ready Set STEM to deliver STEM-accredited coding programs at your school, 
              library, park district, or community center. We handle everything — you just provide the space.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/contact">
                <Button className="bg-rsc-yellow text-rsc-navy hover:bg-rsc-yellow-bright text-lg px-8 py-6 font-bold rounded-full">
                  Become a Partner <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <a href="#testimonials">
                <Button variant="outline" className="text-lg px-8 py-6 text-white border-white hover:bg-white/10">
                  See Partner Stories
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Partner Logos */}
      <section className="py-10 bg-white border-b">
        <div className="container-app">
          <p className="text-center text-sm text-gray-500 font-semibold uppercase tracking-wider mb-6">Trusted by Schools & Organizations Across the Region</p>
          <div className="flex flex-wrap justify-center items-center gap-6 md:gap-10">
            {partnerLogos.map((logo, i) => (
              <div key={i} className="flex items-center gap-2 text-gray-400 hover:text-rsc-navy transition-colors" data-testid={`partner-logo-${i}`}>
                <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center font-bold text-xs">
                  {logo.initials}
                </div>
                <span className="text-sm font-medium hidden md:inline">{logo.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partner Types */}
      <section className="section-padding">
        <div className="container-app">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-rsc-navy mb-4">Who We Partner With</h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              We work with diverse organizations to bring coding education to every corner of the community.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {partnerTypes.map((type, i) => {
              const Icon = type.icon;
              return (
                <Card key={i} className="card-marketing h-full text-center">
                  <CardContent className="p-8">
                    <div className={`w-16 h-16 rounded-2xl ${type.color} flex items-center justify-center mx-auto mb-4`}>
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="font-heading text-xl font-bold text-rsc-navy mb-2">{type.title}</h3>
                    <p className="text-gray-600 text-sm">{type.desc}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Partner Testimonials */}
      <section id="testimonials" className="section-padding bg-rsc-navy text-white">
        <div className="container-app">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">What Our Partners Say</h2>
            <p className="text-white/70 text-lg max-w-2xl mx-auto">
              Hear from school administrators, library directors, and community leaders who have 
              partnered with Ready Set STEM.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {testimonials.map((t, i) => (
              <Card key={i} className="bg-white/5 border-white/10" data-testid={`partner-testimonial-${i}`}>
                <CardContent className="p-8">
                  <Quote className="w-8 h-8 text-rsc-yellow/50 mb-4" />
                  <div className="flex gap-1 mb-4">
                    {[...Array(t.rating)].map((_, s) => (
                      <Star key={s} className="w-4 h-4 fill-rsc-yellow text-rsc-yellow" />
                    ))}
                  </div>
                  <p className="text-white/90 mb-6 italic">"{t.quote}"</p>
                  <div className="border-t border-white/10 pt-4">
                    <p className="font-semibold text-white">{t.author}</p>
                    <p className="text-sm text-white/60">{t.role}</p>
                    <p className="text-sm text-rsc-yellow">{t.org}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Partnership Benefits */}
      <section className="section-padding">
        <div className="container-app">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-rsc-navy mb-4">What's Included in Every Partnership</h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              We handle the curriculum, instructors, and logistics — you provide the space and students.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((b, i) => {
              const Icon = b.icon;
              return (
                <div key={i} className="flex items-start gap-4 p-6 rounded-xl border hover:shadow-md transition-shadow">
                  <div className="w-12 h-12 rounded-xl bg-rsc-blue/10 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-6 h-6 text-rsc-blue" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-rsc-navy mb-1">{b.title}</h3>
                    <p className="text-gray-600 text-sm">{b.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-gradient-to-r from-rsc-blue to-rsc-navy text-white">
        <div className="container-app text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">Ready to Partner With Us?</h2>
          <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
            Let's bring coding education to your community. Schedule a call with our partnerships team 
            to discuss a custom program for your organization.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/contact">
              <Button className="bg-rsc-yellow text-rsc-navy hover:bg-rsc-yellow-bright text-lg px-8 py-6 font-bold rounded-full">
                Schedule a Partnership Call <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/community-impact">
              <Button variant="outline" className="text-lg px-8 py-6 text-white border-white hover:bg-white/10">
                Our Community Impact
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
